import React, { useState, useRef, useCallback } from 'react';
import { 
  Send, 
  Image as ImageIcon,
  Upload,
  AlertCircle,
  CheckCircle,
  Loader2,
  Zap,
  MessageSquare
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useChatStore } from '../../stores/chatStore';
import { apiService } from '../../services/api';
import { QuickActionPopup } from './QuickActionPopup';
import toast from 'react-hot-toast';

interface MessageInputProps {
  userId: number;
  onFastReply?: () => void;
  onQuickAction?: () => void;
  onSlashTyped?: (searchQuery?: string) => void;
  insertMessage?: string;
  onMessageInserted?: () => void;
  clearOnClose?: boolean;
  showQuickActionPopup?: boolean;
  quickActionSearchQuery?: string;
  onQuickActionSelect?: (message: string) => void;
  onQuickActionClose?: () => void;
}

interface UploadingImage {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'sending' | 'success' | 'error';
  error?: string;
  url?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({ 
  userId, 
  onFastReply, 
  onQuickAction, 
  onSlashTyped, 
  insertMessage, 
  onMessageInserted, 
  clearOnClose,
  showQuickActionPopup = false,
  quickActionSearchQuery = '',
  onQuickActionSelect,
  onQuickActionClose
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  const { user } = useAuthStore();
  const { sendMessage, startPolling, stopPolling } = useChatStore();

  // Handle message insertion from Quick Actions
  React.useEffect(() => {
    if (insertMessage) {
      // Replace everything from "/" onwards with the selected message
      const currentMessage = message;
      const slashIndex = currentMessage.indexOf('/');
      
      let newMessage: string;
      if (slashIndex >= 0) {
        // Replace from "/" to end with the new message
        newMessage = currentMessage.substring(0, slashIndex) + insertMessage;
      } else {
        // If no "/" found, just set the message
        newMessage = insertMessage;
      }
      
      setMessage(newMessage);
      
      // Notify parent that message has been inserted
      if (onMessageInserted) {
        onMessageInserted();
      }
      // Focus the textarea after inserting message
      setTimeout(() => {
        textareaRef.current?.focus();
        // Move cursor to end of text
        if (textareaRef.current) {
          const length = newMessage.length;
          textareaRef.current.setSelectionRange(length, length);
        }
        adjustTextareaHeight();
      }, 0);
    }
  }, [insertMessage, onMessageInserted, message]);

  // Clear "/" when popup closes without selection
  React.useEffect(() => {
    if (clearOnClose && message.startsWith('/')) {
      // Remove everything from "/" onwards
      const slashIndex = message.indexOf('/');
      if (slashIndex >= 0) {
        setMessage(message.substring(0, slashIndex));
      }
    }
  }, [clearOnClose, message]);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim()) return;
    if (!user) return;

    try {
      await sendMessage(userId, {
        message: message.trim(),
        type: 'Text'
      });
      setMessage('');
      setIsTyping(false);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [message, user, userId, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Escape key to close Quick Actions if message starts with "/"
    if (e.key === 'Escape' && message.startsWith('/')) {
      setMessage('');
      return;
    }
    
    // Don't send message if user is typing a command (starts with "/")
    if (e.key === 'Enter' && !e.shiftKey && !message.startsWith('/')) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);
    
    // Check if "/" is typed as the first character or if user is typing after "/"
    if (newValue.startsWith('/')) {
      if (onSlashTyped) {
        // Extract search query (everything after "/")
        const searchQuery = newValue.substring(1).trim();
        onSlashTyped(searchQuery);
      }
    } else if (onSlashTyped) {
      // If message no longer starts with "/", close the popup
      // Pass undefined to indicate popup should close
      onSlashTyped(undefined);
    }
    
    // Typing indicator logic
    if (newValue.trim() && !isTyping) {
      setIsTyping(true);
    } else if (!newValue.trim() && isTyping) {
      setIsTyping(false);
    }
  };

  const handleImageSelect = useCallback(async (files: File[]) => {
    if (!files.length) return;

    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    // Pause polling during upload
    stopPolling();

    // Create uploading images
    const newUploadingImages: UploadingImage[] = validFiles.map(file => ({
      id: `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      progress: 0,
      status: 'uploading'
    }));

    setUploadingImages(prev => [...prev, ...newUploadingImages]);

    // Upload images concurrently
    const uploadPromises = validFiles.map(async (file, index) => {
      const uploadingImage = newUploadingImages[index];
      
      try {
        // Upload file
        const result = await apiService.uploadMedia(file, (progress) => {
          setUploadingImages(prev => 
            prev.map(img => 
              img.id === uploadingImage.id 
                ? { ...img, progress }
                : img
            )
          );
        });

        // Update status to sending
        setUploadingImages(prev => 
          prev.map(img => 
            img.id === uploadingImage.id 
              ? { ...img, status: 'sending', url: result.url }
              : img
          )
        );

        // Send image message
        await apiService.sendImageMessage(userId, result.url);

        // Update status to success
        setUploadingImages(prev => 
          prev.map(img => 
            img.id === uploadingImage.id 
              ? { ...img, status: 'success' }
              : img
          )
        );

        // Remove from uploading list after 2 seconds
        setTimeout(() => {
          setUploadingImages(prev => prev.filter(img => img.id !== uploadingImage.id));
        }, 2000);

      } catch (error: any) {
        // Update status to error
        setUploadingImages(prev => 
          prev.map(img => 
            img.id === uploadingImage.id 
              ? { ...img, status: 'error', error: error.message || 'Upload failed' }
              : img
          )
        );
      }
    });

    // Wait for all uploads to complete, then resume polling
    Promise.allSettled(uploadPromises).finally(() => {
      // Resume polling after a short delay
      setTimeout(() => {
        startPolling();
      }, 1000);
    });
  }, [userId, stopPolling, startPolling]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleImageSelect(files);
    
    // Reset input value
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  }, [handleImageSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleImageSelect(files);
  }, [handleImageSelect]);

  const retryUpload = useCallback((uploadingImage: UploadingImage) => {
    setUploadingImages(prev => 
      prev.map(img => 
        img.id === uploadingImage.id 
          ? { ...img, status: 'uploading', progress: 0, error: undefined }
          : img
      )
    );

    handleImageSelect([uploadingImage.file]);
  }, [handleImageSelect]);

  const cancelUpload = useCallback((uploadingImage: UploadingImage) => {
    setUploadingImages(prev => prev.filter(img => img.id !== uploadingImage.id));
  }, []);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const canSend = message.trim() || uploadingImages.length > 0;

  return (
    <div 
      className={`border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 ${isDragOver ? 'bg-blue-50 border-blue-300' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Upload Progress */}
      {uploadingImages.length > 0 && (
        <div className="mb-4 space-y-2">
          {uploadingImages.map((uploadingImage) => (
            <div key={uploadingImage.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <ImageIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {uploadingImage.file.name}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {uploadingImage.status === 'uploading' && (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  )}
                  {uploadingImage.status === 'sending' && (
                    <Loader2 className="h-4 w-4 animate-spin text-yellow-500" />
                  )}
                  {uploadingImage.status === 'success' && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {uploadingImage.status === 'error' && (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  )}
                  
                  {uploadingImage.status === 'error' && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => retryUpload(uploadingImage)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => cancelUpload(uploadingImage)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {(uploadingImage.status === 'uploading' || uploadingImage.status === 'sending') && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      uploadingImage.status === 'uploading' ? 'bg-blue-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${uploadingImage.progress}%` }}
                  />
                </div>
              )}

              {/* Status text */}
              {uploadingImage.status === 'uploading' && (
                <p className="text-xs text-blue-600 mt-1">Uploading...</p>
              )}
              {uploadingImage.status === 'sending' && (
                <p className="text-xs text-yellow-600 mt-1">Sending...</p>
              )}
              {uploadingImage.status === 'success' && (
                <p className="text-xs text-green-600 mt-1">Sent successfully</p>
              )}

              {/* Error message */}
              {uploadingImage.status === 'error' && uploadingImage.error && (
                <p className="text-xs text-red-600 mt-1">
                  {uploadingImage.error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-blue-100 bg-opacity-50 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <p className="text-blue-600 font-medium">Drop images here to upload</p>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="flex items-center space-x-3">
        {/* Attachment Buttons - Hidden when typing */}
        {!message.trim() && (
          <div className="flex items-center space-x-1">
            <button
              onClick={() => imageInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Attach image"
            >
              <ImageIcon className="h-5 w-5" />
            </button>
            
            {/* Fast Reply Button */}
            {onFastReply && (
              <button
                onClick={onFastReply}
                className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                aria-label="Fast Reply"
                title="Fast Reply"
              >
                <Zap className="h-5 w-5" />
              </button>
            )}
            
            {/* Quick Actions Button */}
            {onQuickAction && (
              <button
                onClick={onQuickAction}
                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                aria-label="Quick Actions"
                title="Quick Actions"
              >
                <MessageSquare className="h-5 w-5" />
              </button>
            )}
            
            {/* Emoji Button - Hidden */}
            {/* <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Add emoji"
            >
              <Smile className="h-5 w-5" />
            </button> */}
          </div>
        )}

        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
            aria-label="Message input"
          />
          
          {/* Typing Indicator */}
          {isTyping && (
            <div className="absolute -top-8 left-0 text-xs text-gray-500 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm">
              {user?.name} is typing...
            </div>
          )}

          {/* Quick Action Popup - Attached to input */}
          {showQuickActionPopup && onQuickActionSelect && (
            <div className="absolute bottom-full left-0 right-0 mb-2 z-50">
              <QuickActionPopup
                isOpen={showQuickActionPopup}
                onClose={onQuickActionClose || (() => {})}
                onSelectMessage={onQuickActionSelect}
                searchQuery={quickActionSearchQuery}
                isAttached={true}
              />
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={handleSendMessage}
          disabled={!canSend}
          className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Send message"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>

      {/* Hidden Image Input */}
      <input
        ref={imageInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        accept="image/*"
        aria-label="Select images to upload"
      />

      {/* Emoji Picker (Simple Implementation) */}
      {showEmojiPicker && (
        <div className="absolute bottom-16 left-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 z-10">
          <div className="grid grid-cols-8 gap-1">
            {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰'].map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  setMessage(prev => prev + emoji);
                  setShowEmojiPicker(false);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-lg"
                aria-label={`Add ${emoji} emoji`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Character Count */}
      {message.length > 0 && (
        <div className="mt-2 text-right">
          <span className={`text-xs ${message.length > 1000 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
            {message.length}/2000
          </span>
        </div>
      )}
    </div>
  );
};
